<template>
	<div class="policy-sections-page">
		<!-- Page Header -->
		<div class="page-header">
			<h1>Policy Sections Admin</h1>
			<p class="subtitle">Manage JSON configuration files</p>
		</div>

		<!-- Alerts -->
		<transition name="fade">
			<div v-if="showAlert" class="alert" :class="`alert-${alertVariant}`">
				<span class="alert-message">{{ alertMessage }}</span>
				<button class="alert-close" @click="showAlert = false">&#10005;</button>
			</div>
		</transition>

		<div class="policy-sections-container">
			<!-- Files List Section -->
			<div class="files-list-section">
				<div class="section-header">
					<h2>Config Files</h2>
					<button class="action-btn secondary" @click="loadFiles" :disabled="loading">
						🔄
					</button>
				</div>

				<div class="search-box">
					<input
						type="text"
						v-model="searchQuery"
						placeholder="Search files..."
						class="search-input"
					/>
				</div>

				<div v-if="loading && !files" class="loading">
					<div class="spinner"></div>
					<span>Loading files...</span>
				</div>

				<div v-else-if="filteredFiles && filteredFiles.length > 0" class="files-list">
					<div
						v-for="file in filteredFiles"
						:key="file.fileName"
						class="file-item"
						:class="{ active: selectedFile === file.fileName }"
						@click="selectFile(file)"
					>
						<span class="file-name">{{ file.fileName }}</span>
					</div>
				</div>

				<div v-else class="empty-state">
					<p>No config files found</p>
				</div>
			</div>

			<!-- Editor Section -->
			<div v-if="selectedFile" class="editor-section">
				<div class="section-header">
					<h2>{{ selectedFile }}</h2>
					<AdminHeaderActions
						:loading="saving"
						:has-id="!!selectedFile"
						@save="saveContent"
						@delete="showDeleteConfirm = true"
						@new="showNewFileDialog = true"
					/>
				</div>

				<div v-if="isLoadingContent" class="loading">
					<div class="spinner"></div>
					<span>Loading file content...</span>
				</div>

				<div v-else-if="loadError" class="error-message">
					{{ loadError }}
				</div>

				<div v-else class="editor-wrapper">
					<div class="editor-container" ref="editorContainer"></div>
					<div v-if="hasUnsavedChanges" class="unsaved-indicator">
						Unsaved changes
					</div>
				</div>
			</div>

			<!-- Empty State -->
			<div v-else class="empty-state-section">
				<p>Select a file to edit</p>
			</div>
		</div>

		<!-- Unsaved Changes Warning Dialog -->
		<MGDialog
			v-model="showUnsavedWarning"
			title="Unsaved Changes"
			max-width="400px"
			content-height="auto"
		>
			<template #content>
				<p>You have unsaved changes. Are you sure you want to switch files?</p>
				<p class="text-muted">Your changes will be lost if you proceed.</p>
			</template>
			<template #actions>
				<MGButton text="Discard Changes" @click="handleUnsavedAction('discard')" :stacked="true" />
				<MGButton text="Stay on Current File" :outline="true" @click="handleUnsavedAction('cancel')" :stacked="true" />
			</template>
		</MGDialog>

		<!-- New File Dialog -->
		<MGDialog
			v-model="showNewFileDialog"
			title="Create New Section"
			max-width="400px"
			content-height="auto"
		>
			<template #content>
				<div class="new-file-form">
					<label for="newFileName">File Name</label>
					<input
						id="newFileName"
						type="text"
						v-model="newFileName"
						placeholder="e.g. my-section.json"
						class="form-input"
						@keyup.enter="createNewFile"
					/>
					<p class="input-hint">Must end with .json</p>
				</div>
			</template>
			<template #actions>
				<MGButton text="Create" @click="createNewFile" :stacked="true" :disabled="!isValidFileName" />
				<MGButton text="Cancel" :outline="true" @click="showNewFileDialog = false" :stacked="true" />
			</template>
		</MGDialog>

		<!-- Delete Confirmation Dialog -->
		<MGDialog
			v-model="showDeleteConfirm"
			title="Delete Section"
			max-width="400px"
			content-height="auto"
		>
			<template #content>
				<p>Are you sure you want to delete <strong>{{ selectedFile }}</strong>?</p>
				<p class="text-muted">This action cannot be undone.</p>
			</template>
			<template #actions>
				<MGButton text="Delete" @click="deleteFile" :stacked="true" />
				<MGButton text="Cancel" :outline="true" @click="showDeleteConfirm = false" :stacked="true" />
			</template>
		</MGDialog>
	</div>
</template>

<script>
import { EditorView, basicSetup } from 'codemirror';
import { json } from '@codemirror/lang-json';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorState } from '@codemirror/state';
import AdminServices from '@/admin/mixins/AdminServices';
import MGButton from '@/components/elements/MGButton.vue';
import MGDialog from '@/components/elements/MGDialog.vue';
import AdminHeaderActions from '@/admin/components/AdminHeaderActions.vue';

export default {
	name: 'PolicySectionsPage',
	components: {
		MGButton,
		MGDialog,
		AdminHeaderActions
	},
	mixins: [AdminServices],
	data() {
		return {
			files: null,
			selectedFile: null,
			fileContent: '',
			originalContent: '',
			loading: false,
			isLoadingContent: false,
			saving: false,
			loadError: null,
			showAlert: false,
			alertMessage: '',
			alertVariant: 'success',
			searchQuery: '',
			editor: null,
			showUnsavedWarning: false,
			pendingFileSwitch: null,
			hasUnsavedChanges: false,
			showNewFileDialog: false,
			newFileName: '',
			showDeleteConfirm: false
		};
	},
	computed: {
		filteredFiles() {
			if (!this.files) return null;
			const jsonFiles = this.files.filter(f =>
				f.fileName.toLowerCase().includes('.json')
			);
			if (!this.searchQuery) return jsonFiles;
			return jsonFiles.filter(f =>
				f.fileName.toLowerCase().includes(this.searchQuery.toLowerCase())
			);
		},
		isValidFileName() {
			const name = this.newFileName.trim();
			return name.length > 5 && name.toLowerCase().endsWith('.json');
		}
	},
	methods: {
		loadFiles() {
			this.loading = true;
			this._getConfigFiles(
				(data) => {
					this.files = data;
					this.loading = false;
				},
				(error) => {
					this.showAlertMessage(error, 'error');
					this.loading = false;
				}
			);
		},
		createEditor(content) {
			const container = this.$refs.editorContainer;
			if (!container) {
				console.error('Editor container not found');
				return;
			}

			// Destroy existing editor
			if (this.editor) {
				this.editor.destroy();
				this.editor = null;
			}

			// Clear container
			container.innerHTML = '';

			const self = this;
			const updateListener = EditorView.updateListener.of((update) => {
				if (update.docChanged) {
					const currentContent = update.state.doc.toString();
					self.fileContent = currentContent;
					self.hasUnsavedChanges = currentContent !== self.originalContent;
				}
			});

			this.editor = new EditorView({
				state: EditorState.create({
					doc: content,
					extensions: [
						basicSetup,
						json(),
						oneDark,
						updateListener,
						EditorView.theme({
							'&': { height: '100%' },
							'.cm-scroller': { overflow: 'auto' },
							'.cm-content': { fontFamily: 'monospace' }
						})
					]
				}),
				parent: container
			});
		},
		updateEditorContent(content) {
			if (this.editor) {
				this.editor.dispatch({
					changes: {
						from: 0,
						to: this.editor.state.doc.length,
						insert: content
					}
				});
			}
		},
		async selectFile(file) {
			if (this.hasUnsavedChanges) {
				this.pendingFileSwitch = file;
				this.showUnsavedWarning = true;
				return;
			}
			await this.loadFileContent(file);
		},
		async loadFileContent(file) {
			this.selectedFile = file.fileName;
			this.loadError = null;
			this.hasUnsavedChanges = false;
			this.isLoadingContent = true;

			this._getConfigFileContent(file.fileName,
				async (data) => {
					let content;
					if (data.content) {
						content = atob(data.content);
					} else {
						content = JSON.stringify(data, null, 2);
					}
					this.fileContent = content;
					this.originalContent = content;
					this.isLoadingContent = false;

					await this.$nextTick();
					this.createEditor(content);
				},
				(error) => {
					this.loadError = error;
					this.isLoadingContent = false;
				}
			);
		},
		saveContent() {
			if (!this.selectedFile || !this.editor) return;

			try {
				const content = this.editor.state.doc.toString();
				JSON.parse(content); // Validate JSON

				this.saving = true;
				const base64Content = btoa(content);

				this._saveConfigFile(this.selectedFile, base64Content,
					() => {
						this.originalContent = content;
						this.hasUnsavedChanges = false;
						this.showAlertMessage('File saved successfully', 'success');
						this.saving = false;
					},
					(error) => {
						this.showAlertMessage(error, 'error');
						this.saving = false;
					}
				);
			} catch (error) {
				if (error instanceof SyntaxError) {
					this.showAlertMessage('Invalid JSON format. Please fix the errors before saving.', 'error');
				} else {
					this.showAlertMessage('Error saving file', 'error');
				}
			}
		},
		handleUnsavedAction(action) {
			this.showUnsavedWarning = false;
			if (action === 'discard' && this.pendingFileSwitch) {
				this.hasUnsavedChanges = false;
				this.loadFileContent(this.pendingFileSwitch);
			}
			this.pendingFileSwitch = null;
		},
		showAlertMessage(message, variant = 'success') {
			this.alertMessage = message;
			this.alertVariant = variant;
			this.showAlert = true;
			if (variant === 'success') {
				setTimeout(() => {
					this.showAlert = false;
				}, 4000);
			}
		},
		createNewFile() {
			if (!this.isValidFileName) return;

			const fileName = this.newFileName.trim();
			const initialContent = '{\n  \n}';
			const base64Content = btoa(initialContent);

			this._saveConfigFile(fileName, base64Content,
				() => {
					this.showNewFileDialog = false;
					this.newFileName = '';
					this.showAlertMessage('Section created successfully', 'success');

					// Reload files and select the new one
					this._getConfigFiles(
						(data) => {
							this.files = data;
							// Select the newly created file
							const newFile = data.find(f => f.fileName === fileName);
							if (newFile) {
								this.loadFileContent(newFile);
							}
						},
						(error) => {
							this.showAlertMessage(error, 'error');
						}
					);
				},
				(error) => {
					this.showAlertMessage(error, 'error');
				}
			);
		},
		deleteFile() {
			if (!this.selectedFile) return;

			const fileName = this.selectedFile;

			this._deleteConfigFile(fileName,
				() => {
					this.showDeleteConfirm = false;
					this.showAlertMessage('Section deleted successfully', 'success');

					// Clear editor
					if (this.editor) {
						this.editor.destroy();
						this.editor = null;
					}
					this.selectedFile = null;
					this.fileContent = '';
					this.originalContent = '';
					this.hasUnsavedChanges = false;

					// Reload files
					this.loadFiles();
				},
				(error) => {
					this.showDeleteConfirm = false;
					this.showAlertMessage(error, 'error');
				}
			);
		}
	},
	mounted() {
		this.loadFiles();
	},
	beforeUnmount() {
		if (this.editor) {
			this.editor.destroy();
		}
	}
};
</script>

<style scoped>
.policy-sections-page {
	min-height: 100%;
	padding: 20px;
}

/* Header */
.page-header {
	margin-bottom: 30px;
}

.page-header h1 {
	margin: 0 0 5px 0;
	color: var(--admin-text-primary);
	font-size: 28px;
	font-weight: 600;
}

.subtitle {
	margin: 0;
	color: var(--admin-text-secondary);
	font-size: 14px;
}

/* Alerts */
.alert {
	margin-bottom: 20px;
	padding: 12px 15px;
	border-radius: var(--border-radius-s);
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.alert-success {
	background-color: rgba(76, 175, 80, 0.1);
	color: #2e7d32;
	border: 1px solid rgba(76, 175, 80, 0.3);
}

.alert-error {
	background-color: rgba(211, 47, 47, 0.1);
	color: #c62828;
	border: 1px solid rgba(211, 47, 47, 0.3);
}

.alert-close {
	background: none;
	border: none;
	color: inherit;
	cursor: pointer;
	font-size: 16px;
	padding: 0;
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
}

/* Container */
.policy-sections-container {
	display: grid;
	grid-template-columns: 300px 1fr;
	gap: 20px;
	height: calc(100vh - 250px);
}

/* Files List Section */
.files-list-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 20px;
	box-shadow: var(--box-shadow);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.section-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 15px;
	border-bottom: 1px solid var(--admin-border);
	padding-bottom: 15px;
}

.section-header h2 {
	margin: 0;
	color: var(--admin-text-primary);
	font-size: 18px;
	font-weight: 600;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.header-actions {
	display: flex;
	gap: 10px;
}

.search-box {
	margin-bottom: 15px;
}

.search-input {
	width: 100%;
	padding: 10px 12px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-bg-primary);
	color: var(--admin-text-primary);
	font-size: 14px;
}

.search-input::placeholder {
	color: var(--admin-text-secondary);
}

.search-input:focus {
	outline: none;
	border-color: var(--admin-text-primary);
}

.loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 20px;
	color: var(--admin-text-secondary);
}

.spinner {
	border: 3px solid var(--admin-border);
	border-top: 3px solid var(--admin-text-primary);
	border-radius: 50%;
	width: 40px;
	height: 40px;
	animation: spin 1s linear infinite;
	margin-bottom: 10px;
}

@keyframes spin {
	0% { transform: rotate(0deg); }
	100% { transform: rotate(360deg); }
}

.files-list {
	flex: 1;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.file-item {
	padding: 12px;
	background-color: var(--admin-bg-primary);
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	cursor: pointer;
	transition: all 0.2s ease;
}

.file-item:hover {
	background-color: var(--admin-hover-bg);
	border-color: var(--admin-text-primary);
}

.file-item.active {
	background-color: var(--admin-active-bg);
	border-color: var(--background-color-alt6);
	border-left: 4px solid var(--background-color-alt6);
}

.file-name {
	color: var(--admin-text-primary);
	font-size: 13px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.empty-state {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 200px;
	color: var(--admin-text-secondary);
	text-align: center;
}

/* Editor Section */
.editor-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 20px;
	box-shadow: var(--box-shadow);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.editor-wrapper {
	flex: 1;
	display: flex;
	flex-direction: column;
	position: relative;
	min-height: 0;
}

.editor-container {
	flex: 1;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	overflow: hidden;
	min-height: 400px;
}

/* CodeMirror styling */
.editor-container :deep(.cm-editor) {
	height: 100%;
	font-size: 14px;
}

.editor-container :deep(.cm-scroller) {
	font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
}

.unsaved-indicator {
	position: absolute;
	top: 10px;
	right: 10px;
	background-color: rgba(255, 193, 7, 0.9);
	color: #000;
	padding: 4px 12px;
	border-radius: var(--border-radius-s);
	font-size: 12px;
	font-weight: 600;
	z-index: 10;
}

.error-message {
	color: #d32f2f;
	background-color: rgba(211, 47, 47, 0.1);
	border: 1px solid rgba(211, 47, 47, 0.3);
	border-radius: var(--border-radius-s);
	padding: 20px;
	text-align: center;
}

.empty-state-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 40px 20px;
	box-shadow: var(--box-shadow);
	display: flex;
	align-items: center;
	justify-content: center;
	color: var(--admin-text-secondary);
	text-align: center;
}

.text-muted {
	color: var(--admin-text-secondary);
	font-size: 14px;
}

/* Action Buttons */
.action-btn {
	padding: 10px 16px;
	border: none;
	border-radius: var(--border-radius-s);
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
}

.action-btn.secondary {
	background-color: var(--admin-hover-bg);
	color: var(--admin-text-primary);
	border: 1px solid var(--admin-border);
}

.action-btn.secondary:hover:not(:disabled) {
	background-color: var(--admin-active-bg);
}

.action-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

/* New File Form */
.new-file-form {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.new-file-form label {
	font-weight: 600;
	color: var(--admin-text-primary);
}

.form-input {
	width: 100%;
	padding: 12px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-bg-primary);
	color: var(--admin-text-primary);
	font-size: 14px;
}

.form-input:focus {
	outline: none;
	border-color: var(--background-color-alt6);
}

.input-hint {
	margin: 0;
	font-size: 12px;
	color: var(--admin-text-secondary);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

/* Responsive */
@media (max-width: 1024px) {
	.policy-sections-container {
		grid-template-columns: 250px 1fr;
	}
}

@media (max-width: 768px) {
	.policy-sections-page {
		padding: 10px;
	}

	.policy-sections-container {
		grid-template-columns: 1fr;
		height: auto;
	}

	.files-list-section {
		max-height: 300px;
	}

	.editor-section {
		min-height: 400px;
	}

	.section-header {
		flex-direction: column;
		align-items: flex-start;
		gap: 10px;
	}

	.section-header h2 {
		font-size: 16px;
	}
}

/* Light mode overrides */
:root.light-mode .files-list-section {
	background-color: #ffffff !important;
	border: 1px solid #e0e0e0 !important;
}

:root.light-mode .editor-section {
	background-color: #ffffff !important;
	border: 1px solid #e0e0e0 !important;
}

:root.light-mode .file-item {
	background-color: #ffffff !important;
	border: 1px solid #e0e0e0 !important;
}

:root.light-mode .file-item.active {
	background-color: #f0f0f0 !important;
	border-color: var(--background-color-alt6) !important;
}

:root.light-mode .search-input {
	background-color: #ffffff !important;
	border: 1px solid #e0e0e0 !important;
}
</style>
